import { apiFetch } from "./apiClient";
import type { AuditFields } from "./audit";

/** A supplier's GST registration type (the API's GstRegistrationType.ForSupplier). */
export type BranchGstRegistrationType =
  | "REGULAR"
  | "COMPOSITION"
  | "SEZ_UNIT"
  | "SEZ_DEVELOPER"
  | "CASUAL"
  | "OTHER";

export const BRANCH_GST_REGISTRATION_OPTIONS: {
  value: BranchGstRegistrationType;
  label: string;
}[] = [
  { value: "REGULAR", label: "Regular" },
  { value: "COMPOSITION", label: "Composition" },
  { value: "SEZ_UNIT", label: "SEZ unit" },
  { value: "SEZ_DEVELOPER", label: "SEZ developer" },
  { value: "CASUAL", label: "Casual taxable person" },
  { value: "OTHER", label: "Other" },
];

/** Maps to the backend's BranchDto — the real, live Branches resource. */
export interface BranchDto extends AuditFields {
  branchId: number;
  accountId: number;
  branchCode: string | null;
  branchName: string | null;
  contactPerson: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  zipCode: string | null;
  gstin: string | null;
  pan: string | null;
  /** Supplier GST profile for e-invoicing. */
  legalName: string | null;
  tradeName: string | null;
  addressLine2: string | null;
  gstRegistrationType: BranchGstRegistrationType | string | null;
  /** E-invoicing applies to this branch's GSTIN. */
  eInvoiceEnabled: boolean;
  /** yyyy-MM-dd */
  eInvoiceApplicableFrom: string | null;
  currencyCode: string | null;
  timeZone: string | null;
  logoUrl: string | null;
  /** Printed beside the main logo on the invoice, when set. */
  secondaryLogoUrl?: string | null;
  statusName: string | null;
  isHeadOffice: boolean;
  isActive: boolean;
}

export interface BranchPayload {
  accountId: number;
  branchCode?: string | null;
  branchName?: string | null;
  contactPerson?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  zipCode?: string | null;
  gstin?: string | null;
  pan?: string | null;
  /**
   * Supplier GST profile. Null/absent keeps the stored value; "" clears a
   * text field. Switching e-invoicing on needs a valid GSTIN, legal name,
   * address, city and 6-digit PIN — the API answers 400 with what is missing.
   * An applicable-from date can be changed but not cleared by omission.
   */
  legalName?: string | null;
  tradeName?: string | null;
  addressLine2?: string | null;
  gstRegistrationType?: string | null;
  eInvoiceEnabled?: boolean | null;
  eInvoiceApplicableFrom?: string | null;
  currencyCode?: string | null;
  timeZone?: string | null;
  logoUrl?: string | null;
  statusName?: string | null;
  isHeadOffice: boolean;
  isActive: boolean;
}

/** The live endpoint filters server-side on accountId (unlike Roles/Users,
 * which return everything and let the caller filter) — omitting it returns
 * an empty list rather than "all branches". */
export const listBranches = async (accountId: number): Promise<BranchDto[]> => {
  const res = await apiFetch(`/branches?accountId=${accountId}`);
  return res.json();
};

export const createBranch = async (
  payload: BranchPayload
): Promise<BranchDto> => {
  const res = await apiFetch("/branches", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return res.json();
};

export const updateBranch = async (
  branchId: number,
  payload: BranchPayload
): Promise<void> => {
  await apiFetch(`/branches/${branchId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
};

/** Uploads the branch's logo (PNG, JPG or WEBP, up to 2 MB); the server keeps it
 * under uploads/{BranchCode}/logo/ (the secondary one under logo-secondary/) and
 * points the branch's logoUrl / secondaryLogoUrl at it. */
export const uploadBranchLogo = async (branchId: number, file: File, secondary = false): Promise<BranchDto> => {
  const form = new FormData();
  form.append("file", file, file.name);
  const res = await apiFetch(`/branches/${branchId}/logo${secondary ? "/secondary" : ""}`, {
    method: "POST",
    body: form,
  });
  return res.json();
};

export const deleteBranchLogo = async (branchId: number, secondary = false): Promise<BranchDto> => {
  const res = await apiFetch(`/branches/${branchId}/logo${secondary ? "/secondary" : ""}`, { method: "DELETE" });
  return res.json();
};

export const deleteBranch = async (branchId: number): Promise<void> => {
  await apiFetch(`/branches/${branchId}`, { method: "DELETE" });
};
